import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import toast from 'react-hot-toast';

interface SavedBlogPost {
  id: string;
  title: string;
  content: string;
  topic: string;
  keywords?: string[];
  length: string;
  tone: string;
  targetAudience: string;
  createdAt: string;
  updatedAt: string;
}

interface ContentLibraryTabProps {
  onEditPost?: (post: SavedBlogPost) => void;
}

const ContentLibraryTab: React.FC<ContentLibraryTabProps> = ({ onEditPost }) => {
  const { user } = useAuth();
  const [posts, setPosts] = useState<SavedBlogPost[]>([]);
  const [filteredPosts, setFilteredPosts] = useState<SavedBlogPost[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterLength, setFilterLength] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [selectedPost, setSelectedPost] = useState<SavedBlogPost | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  // Load posts from localStorage
  useEffect(() => {
    loadPosts();
  }, [user]);

  // Filter posts when search or filter changes
  useEffect(() => {
    filterPosts();
  }, [searchQuery, filterLength, posts]);

  const loadPosts = () => {
    if (!user) return;
    
    const savedPosts = localStorage.getItem(`seo_content_library_${user.id}`);
    if (savedPosts) {
      try {
        const parsed = JSON.parse(savedPosts);
        setPosts(parsed);
      } catch (error) {
        console.error('Error loading posts:', error);
        setPosts([]);
      }
    }
  };

  const filterPosts = () => {
    let filtered = [...posts];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(post =>
        post.title.toLowerCase().includes(query) ||
        post.topic.toLowerCase().includes(query) ||
        post.keywords?.some(k => k.toLowerCase().includes(query))
      );
    }

    // Length filter
    if (filterLength !== 'all') {
      filtered = filtered.filter(post => post.length === filterLength);
    }

    // Sort by most recent
    filtered.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    setFilteredPosts(filtered);
  };

  const handleDelete = (id: string) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    const updated = posts.filter(p => p.id !== id);
    setPosts(updated);
    localStorage.setItem(`seo_content_library_${user?.id}`, JSON.stringify(updated));
    toast.success('Post deleted');
  };

  const handleExport = (post: SavedBlogPost, format: 'html' | 'text') => {
    const content = format === 'html'
      ? `<h1>${post.title}</h1>\n${post.content}`
      : `${post.title}\n\n${post.content.replace(/<[^>]*>/g, '')}`;

    const blob = new Blob([content], { type: format === 'html' ? 'text/html' : 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${post.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.${format === 'html' ? 'html' : 'txt'}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported as ${format.toUpperCase()}`);
  };

  const handleCopy = (post: SavedBlogPost, format: 'html' | 'text') => {
    const content = format === 'html'
      ? `<h1>${post.title}</h1>\n${post.content}`
      : `${post.title}\n\n${post.content.replace(/<[^>]*>/g, '')}`;

    navigator.clipboard.writeText(content);
    toast.success(`Copied as ${format.toUpperCase()}`);
  };

  const handleView = (post: SavedBlogPost) => {
    setSelectedPost(post);
    setShowPreview(true);
  };

  // Empty state
  if (posts.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="max-w-md mx-auto">
          <div className="mb-6">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
          
          <h3 className="text-2xl font-bold text-gray-900 mb-3">
            No Content Yet
          </h3>
          <p className="text-gray-600 mb-8">
            Your saved blog posts will appear here. Create your first post in the Blog Creator tab!
          </p>

          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">💡 Tip:</h4>
            <p className="text-sm text-gray-700">
              Posts are automatically saved when you generate them in the Blog Creator tab. 
              Come back here to view, edit, export, or delete your content library.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header with search and filters */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Content Library</h3>
            <p className="text-sm text-gray-600">
              {filteredPosts.length} {filteredPosts.length === 1 ? 'post' : 'posts'}
              {searchQuery && ` matching "${searchQuery}"`}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}
              title="List view"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}
              title="Grid view"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <input
              type="text"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Search by title, topic, or keywords..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <select
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={filterLength}
            onChange={(e) => setFilterLength(e.target.value)}
          >
            <option value="all">All Lengths</option>
            <option value="Short (300-500 words)">Short</option>
            <option value="Medium (800-1200 words)">Medium</option>
            <option value="Long (1500-2000 words)">Long</option>
          </select>
        </div>
      </div>

      {/* Content grid/list */}
      {viewMode === 'list' ? (
        <div className="space-y-4">
          {filteredPosts.map((post) => (
            <div key={post.id} className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="text-lg font-bold text-gray-900 mb-2">{post.title}</h4>
                  <div className="flex flex-wrap gap-2 mb-3">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                      {post.length}
                    </span>
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                      {post.tone}
                    </span>
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                      {post.targetAudience}
                    </span>
                  </div>
                  {post.keywords && post.keywords.length > 0 && (
                    <div className="text-sm text-gray-600 mb-2">
                      Keywords: {post.keywords.join(', ')}
                    </div>
                  )}
                  <div className="text-xs text-gray-500">
                    Created: {new Date(post.createdAt).toLocaleDateString()}
                  </div>
                </div>

                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => handleView(post)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                    title="View"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleCopy(post, 'html')}
                    className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg"
                    title="Copy HTML"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleExport(post, 'html')}
                    className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg"
                    title="Export"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDelete(post.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    title="Delete"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPosts.map((post) => (
            <div key={post.id} className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
              <h4 className="text-base font-bold text-gray-900 mb-2 line-clamp-2">{post.title}</h4>
              <div className="flex flex-wrap gap-1 mb-2">
                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                  {post.length.split(' ')[0]}
                </span>
                <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                  {post.tone}
                </span>
              </div>
              <div className="text-xs text-gray-500 mb-3">
                {new Date(post.createdAt).toLocaleDateString()}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleView(post)}
                  className="flex-1 py-1.5 px-2 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  View
                </button>
                <button
                  onClick={() => handleCopy(post, 'html')}
                  className="p-1.5 text-gray-600 hover:bg-gray-100 rounded"
                  title="Copy"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
                <button
                  onClick={() => handleDelete(post.id)}
                  className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                  title="Delete"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && selectedPost && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={() => setShowPreview(false)}>
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">Preview</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <h1 className="text-3xl font-bold mb-6">{selectedPost.title}</h1>
              <div
                className="prose max-w-none"
                dangerouslySetInnerHTML={{ __html: selectedPost.content }}
              />
            </div>
            <div className="flex items-center justify-end gap-2 p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => handleCopy(selectedPost, 'text')}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-white"
              >
                Copy Text
              </button>
              <button
                onClick={() => handleCopy(selectedPost, 'html')}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-white"
              >
                Copy HTML
              </button>
              <button
                onClick={() => handleExport(selectedPost, 'html')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Export
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentLibraryTab;
